import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { v4 as uuidv4 } from "uuid";

import { DATE_FORMATS_FNS } from "components";
import { useSelectedLocation } from "components/DealersAndLocationsDropdown/hooks";
import { useNoteSuggestionMutation } from "modules/NoteSuggestions/hooks/useNoteSuggestionMutation";
import { formatCurrentUTCDate } from "utils/dateHelperFunctions";

import type { UniqueIdentifier } from "@dnd-kit/core";
import type { NoteModalProps } from "modules/NoteSuggestions/components";
import type { SortableItems } from "modules/NoteSuggestions/components/SortableList";

export const useNoteSuggestionsModal = ({ noteTypesWithSuggestions, openedModalNoteType, closeModal }: NoteModalProps) => {
  const { t } = useTranslation();

  const selectedNote = openedModalNoteType && noteTypesWithSuggestions.find(nyws => nyws.note_suggestion_type.note_suggestion_type_id === openedModalNoteType);
  const extendedSuggestions = selectedNote?.note_suggestions.map(suggestion => ({ ...suggestion, id: uuidv4() })) ?? [];

  const { selectedLocation } = useSelectedLocation();

  const [items, setItems] = useState<SortableItems[]>(extendedSuggestions);
  const [noteVisibility, setNoteVisibility] = useState(selectedNote?.note_suggestion_type.active || false);

  const { mutate: updateNoteSuggestions } = useNoteSuggestionMutation();

  useEffect(() => {
    setItems(extendedSuggestions);
    setNoteVisibility(selectedNote?.note_suggestion_type.active || false);
  }, [selectedNote, openedModalNoteType]);

  const handleRemoveItem = (id: UniqueIdentifier) =>
    setItems(prev => prev.filter(suggestion => suggestion.id !== id).map((suggestion, index) => ({ ...suggestion, ordering: index + 1 })));

  const handleContentEdit = (id: UniqueIdentifier, content: string) =>
    setItems(prev => prev.map(suggestion => (suggestion.id === id ? { ...suggestion, content } : { ...suggestion })));

  const handleAddItem = () => {
    setItems(prev => {
      const newSuggestion = {
        id: uuidv4(),
        ordering: prev.length + 2,
        content: "",
        note_suggestion_type_id: openedModalNoteType ?? -1,
        note: "",
        created_on: formatCurrentUTCDate(DATE_FORMATS_FNS.ISOFormat),
        updated_on: formatCurrentUTCDate(DATE_FORMATS_FNS.ISOFormat),
        dealer_location_id: selectedLocation?.id ?? -1,
        active: true
      };
      return [...prev, newSuggestion];
    });
  };

  const handleVisibleInNote = () => setNoteVisibility(prev => !prev);
  const handleOnSave = () => {
    if (!selectedNote) return;
    if (items.some(item => !item.content)) return toast.error(t("v8_content_is_mandatory", "Content is mandatory"));
    if (items.length < 3) return toast.error(t("v8_minimum_three_suggestions", "Minimum of 3 suggestions are required"));
    updateNoteSuggestions(
      { ...selectedNote, note_suggestions: items, note_suggestion_type: { ...selectedNote.note_suggestion_type, active: noteVisibility } },
      {
        onSuccess: () => {
          setItems([]);
          closeModal();
        }
      }
    );
  };

  return { items, handleOnSave, handleVisibleInNote, handleAddItem, handleRemoveItem, handleContentEdit, noteVisibility, setItems };
};
